import { memo, useEffect, useRef, useState } from 'react';
import styles from './AnimatedDigits.module.css';

interface AnimatedDigitsProps {
  value: string | number;
  className?: string;
  duration?: number; // Длительность анимации в мс (default: 400)
}

/**
 * Компонент для анимированного отображения числа
 * При изменении значения число быстро "прокручивается" от старого к новому
 */
const AnimatedDigits = ({ value, className, duration = 400 }: AnimatedDigitsProps) => {
  const stringValue = String(value);
  const [displayValue, setDisplayValue] = useState(stringValue);
  const animationRef = useRef<number | null>(null);
  const prevValueRef = useRef<string>(stringValue);
  
  useEffect(() => {
    const prevValue = prevValueRef.current;
    
    if (prevValue === stringValue) return;
    
    prevValueRef.current = stringValue;
    
    // Отменяем предыдущую анимацию
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    // Проверяем форматы
    const slashMatch = stringValue.match(/^(\d+)\/(\d+)$/);
    const prevSlashMatch = prevValue.match(/^(\d+)\/(\d+)$/);
    
    // Формат "X/Y" для лимитов - оба значения должны быть в этом формате
    if (slashMatch && prevSlashMatch) {
      const startNum = parseInt(prevSlashMatch[1]);
      const endNum = parseInt(slashMatch[1]);
      const suffix = `/${slashMatch[2]}`;
      
      animateNumber(startNum, endNum, suffix);
      return;
    }
    
    // Если форматы разные (один X/Y, другой просто число) - просто меняем без анимации
    if (slashMatch || prevSlashMatch) {
      setDisplayValue(stringValue);
      return;
    }
    
    // Оба значения - чистые числа
    const isCurrentPureNumber = /^\d+$/.test(stringValue);
    const isPrevPureNumber = /^\d+$/.test(prevValue);
    
    if (isCurrentPureNumber && isPrevPureNumber) {
      const startNum = parseInt(prevValue);
      const endNum = parseInt(stringValue);
      animateNumber(startNum, endNum, '');
      return;
    }
    
    // Если не числа - просто меняем значение
    setDisplayValue(stringValue);
    
    function animateNumber(start: number, end: number, suffix: string) {
      const startTime = performance.now();
      const diff = end - start;
      
      // Если разница 0 - просто устанавливаем значение
      if (diff === 0) {
        setDisplayValue(`${end}${suffix}`);
        return;
      }
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Используем easeOutExpo для более эффектной анимации
        const eased = 1 - Math.pow(1 - progress, 3);
        
        const currentValue = Math.round(start + diff * eased);
        setDisplayValue(`${currentValue}${suffix}`);
        
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setDisplayValue(`${end}${suffix}`);
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [stringValue, duration]);
  
  // Cleanup при unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <span className={`${styles.container} ${className || ''}`}>
      {displayValue}
    </span>
  );
};

export default memo(AnimatedDigits);
